import {remotePartError, RemotePartMutation} from '@dv5/api'
import {
    ButtonAction,
    clipboardService,
    ErrorDisplayProcessor,
    ExtAddButton,
    LayoutContentTitle,
    UseTemplate
} from "@dv5/components"
import {
    DocumentPartTemplateData,
    DvDocument,
    getDocumentDescriptions,
    getDocumentParts,
    getViewItemUrl,
    ItemAction,
    LayoutContentType,
    PartType,
    requestDescriptionEdit,
    Template, TemplateItemParams,
    TemplateType,
    useAppSelector,
    useAppDispatch
} from '@dv5/domain'
import {toolsService} from '@dv5/quick-tools'
import React, {FC, useEffect, useRef, useState} from "react"
import {ExportMarkdownDialog} from "./panels/ExportMarkdownDialog"
import {remoteDocumentPartsService} from './services/remoteDocumentPartsService'
import {documentApi} from "./services/documentService"
import {CreatePartFromTemplateMessage, PrependDocumentPartMessage, PrependPartFromTemplateMessage} from "./types"

interface Props {
    document: DvDocument
    remote?: boolean
    showRemoteInfo?: boolean
    onToggleRemoteInfo?: () => void
    layoutId: string
    layers: Array<string>
    tags: Array<string>
    onShowDialog: (show: boolean) => void
    onSelectLayout: () => void
    iconColor?: string
}

export const DocumentHeader: FC<Props> = ({
                                              document,
                                              remote = false,
                                              showRemoteInfo = false,
                                              onToggleRemoteInfo,
                                              layoutId,
                                              layers,
                                              tags,
                                              onShowDialog,
                                              onSelectLayout,
                                              iconColor,
                                          }) => {
    const [remoteError, setRemoteError] = useState('')
    const [remoteBusy, setRemoteBusy] = useState(false)
    const pendingRemote = useRef<RemotePartMutation | null>(null)
    const remoteSubmitting = useRef(false)
    const activeDocument = useRef(document.id)
    activeDocument.current = document.id
    useEffect(() => {
        pendingRemote.current = null
        remoteSubmitting.current = false
        setRemoteBusy(false)
        setRemoteError('')
    }, [document.id])
    const createRemote = async (prepend = false) => {
        if (remoteSubmitting.current) return
        remoteSubmitting.current = true;
        setRemoteBusy(true);
        setRemoteError('')
        pendingRemote.current ??= {
            requestId: crypto.randomUUID(), prepend, part: {
                id: crypto.randomUUID(), partContent: '', partHeader: '', partType: PartType.MARKDOWN,
                partIndex: 0, layers: layers.join(','), tags: tags.join(','), commentsCount: 0, revision: 0,
            }
        }
        const documentId = document.id
        try {
            await remoteDocumentPartsService.create(documentId, pendingRemote.current)
            if (activeDocument.current === documentId) pendingRemote.current = null
        } catch (failure) {
            if (activeDocument.current === documentId) setRemoteError(remotePartError(failure))
        } finally {
            if (activeDocument.current === documentId) {
                remoteSubmitting.current = false;
                setRemoteBusy(false)
            }
        }
    }
    const parts = useAppSelector(getDocumentParts)
    const descriptions = useAppSelector(getDocumentDescriptions)
    const hasDescription = (descriptions[document.id] || '').trim() !== ''
    const [showUseTemplate, setShowUseTemplate] = useState(false)
    const [templateAction, setTemplateAction] = useState<ItemAction | null>(null)
    const [showExportMarkdown, setShowExportMarkdown] = useState(false)
    const dispatch = useAppDispatch()
    const markdownInputRef = useRef<HTMLInputElement>(null)
    const textInputRef = useRef<HTMLInputElement>(null)

    const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>, partType: PartType.MARKDOWN | PartType.PLAINTEXT) => {
        const file = event.currentTarget.files?.[0]
        event.currentTarget.value = ''
        if (!file) return

        try {
            await documentApi.importPartFromFile(file, document.id, parts[document.id] || [], layers, tags, partType)
        } catch (error) {
            new ErrorDisplayProcessor(dispatch).displayError(error instanceof Error ? error.message : 'Could not import the file. Please try again.')
        }
    }

    const handleAdd = () => {
        if (remote) {
            void createRemote();
            return
        }
        const payload = documentApi.buildAddPayload(document.id, parts[document.id] || [], layers, tags, PartType.MARKDOWN, null)
        documentApi.addPart(payload)
    }

    const handlePrepend = () => {
        if (remote) {
            void createRemote(true);
            return
        }
        const message: PrependDocumentPartMessage = {
            documentId: document.id,
            partType: PartType.MARKDOWN,
            layers: layers.join(','),
            tags: tags.join(','),
        }
        documentApi.prependPart(message)
    }

    const handlePaste = () => {
        const diaryContent = toolsService.getDiaryContent()
        if (diaryContent) {
            const payload = documentApi.buildAddPayload(document.id, parts[document.id] || [], layers, tags, PartType.DIARY, diaryContent.source)
            documentApi.addPart(payload)
        } else {
            const noteContent = toolsService.getNoteContent()
            if (noteContent) {
                const partType = noteContent.type === 'markdown' ? PartType.MARKDOWN : PartType.PLAINTEXT
                const payload = documentApi.buildAddPayload(document.id, parts[document.id] || [], layers, tags, partType, noteContent.source)
                documentApi.addPart(payload)
            }
        }
    }

    const handleAddFromTemplateTop = () => {
        setTemplateAction(ItemAction.ADD_FROM_TEMPLATE_TOP)
        setShowUseTemplate(true)
        onShowDialog(true)
    }

    const handleAddFromTemplateBottom = () => {
        setTemplateAction(ItemAction.ADD_FROM_TEMPLATE_BOTTOM)
        setShowUseTemplate(true)
        onShowDialog(true)
    }

    const handleTemplateSelect = (template: Template, params: TemplateItemParams) => {
        setShowUseTemplate(false)
        if (template.templateType === TemplateType.DOCUMENT_PART) {
            if (templateAction === ItemAction.ADD_FROM_TEMPLATE_TOP) {
                const message: PrependPartFromTemplateMessage = {
                    documentId: document.id,
                    templateId: template.id,
                    layers: layers.join(','),
                    tags: tags.join(','),
                    params,
                }
                documentApi.prependPartFromTemplate(message)
            } else if (templateAction === ItemAction.ADD_FROM_TEMPLATE_BOTTOM) {
                const message: CreatePartFromTemplateMessage = {
                    documentId: document.id,
                    templateId: template.id,
                    partIndex: documentApi.getNextPartIndex(parts[document.id] || []),
                    layers: layers.join(','),
                    tags: tags.join(','),
                    params,
                }
                documentApi.addPartFromTemplate(message)
            }
        }
        setTemplateAction(null)
    }

    const handleTemplateCancel = () => {
        setShowUseTemplate(false)
        setTemplateAction(null)
    }

    const handleExportMarkdown = () => {
        setShowExportMarkdown(true)
        onShowDialog(true)
    }

    const handleExportMarkdownClose = () => {
        setShowExportMarkdown(false)
        onShowDialog(false)
    }

    const addDocumentPart = (action: ItemAction) => {
        switch (action) {
            case ItemAction.TOGGLE_REMOTE_DOCUMENT_INFO:
                onToggleRemoteInfo?.()
                return
            case ItemAction.ADD:
                return handleAdd()
            case ItemAction.PREPEND:
                return handlePrepend()
            case ItemAction.PASTE:
                return handlePaste()
            case ItemAction.ADD_FROM_TEMPLATE_TOP:
                return handleAddFromTemplateTop()
            case ItemAction.ADD_FROM_TEMPLATE_BOTTOM:
                return handleAddFromTemplateBottom()
            case ItemAction.COPY_URL:
                clipboardService.copyTo(getViewItemUrl(window.location.origin, layoutId, document.id, LayoutContentType.DOCUMENT), false)
                return
            case ItemAction.COPY_ID:
                clipboardService.copyTo(document.id, false)
                return
            case ItemAction.EXPORT_MARKDOWN:
                return handleExportMarkdown()
            case ItemAction.IMPORT_MARKDOWN:
                markdownInputRef.current?.click()
                return
            case ItemAction.IMPORT_TEXT:
                textInputRef.current?.click()
                return
            case ItemAction.EDIT_DESCRIPTION:
                dispatch(requestDescriptionEdit({documentId: document.id}))
                return
        }
    }

    const actions: Array<ButtonAction> = [{
        text: 'Add to the bottom',
        action: ItemAction.ADD,
    }, {
        text: 'Add to the top',
        action: ItemAction.PREPEND,
    }, {
        text: 'To the top from template...',
        action: ItemAction.ADD_FROM_TEMPLATE_TOP,
        separatorBefore: true,
    }, {
        text: 'To the bottom from template...',
        action: ItemAction.ADD_FROM_TEMPLATE_BOTTOM,
    }, {
        text: 'Copy URL',
        action: ItemAction.COPY_URL,
        separatorBefore: true,
    }, {
        text: 'Copy ID',
        action: ItemAction.COPY_ID,
    }, {
        text: 'Export to Markdown',
        action: ItemAction.EXPORT_MARKDOWN,
        separatorBefore: true,
    }, {
        text: 'Improve from Markdown',
        action: ItemAction.IMPORT_MARKDOWN,
    }, {
        text: 'Import from text',
        action: ItemAction.IMPORT_TEXT,
    }, {
        text: hasDescription ? 'Edit description' : 'Add description',
        action: ItemAction.EDIT_DESCRIPTION,
        separatorBefore: true,
    }]

    if (toolsService.getDiaryContent() || toolsService.getNoteContent()) {
        const diaryContent = toolsService.getDiaryContent()
        actions.push({
            text: diaryContent ? 'Add from diary tool' : 'Add from note tool',
            action: ItemAction.PASTE,
            separatorBefore: true,
        })
    }

    if (remote) return <>
        <LayoutContentTitle title={document.title} onSelect={onSelectLayout}/>
        <ExtAddButton actions={[...actions.slice(0, 2), {
            text: showRemoteInfo ? 'Hide document information' : 'Show document information',
            action: ItemAction.TOGGLE_REMOTE_DOCUMENT_INFO,
            separatorBefore: true,
        }]} onAdd={addDocumentPart} tooltipText="Add part"
                      onShowPopup={onShowDialog} iconColor={iconColor}/>
        {remoteBusy && <span role="status">Creating part on agent…</span>}
        {remoteError && <span role="alert">{remoteError}
            <button type="button" onClick={() => void createRemote()}>Retry</button></span>}
    </>

    return (
        <>
            <LayoutContentTitle title={document.title} onSelect={onSelectLayout}/>
            <ExtAddButton actions={actions} onAdd={addDocumentPart} tooltipText="Add part" onShowPopup={onShowDialog}
                          iconColor={iconColor}/>
            <input ref={markdownInputRef} type="file" accept=".md" hidden aria-label="Improve from Markdown"
                   onChange={event => {
                       void handleImportFile(event, PartType.MARKDOWN)
                   }}/>
            <input ref={textInputRef} type="file" accept=".txt" hidden aria-label="Import from text"
                   onChange={event => {
                       void handleImportFile(event, PartType.PLAINTEXT)
                   }}/>
            {showUseTemplate && (
                <UseTemplate
                    templateTypes={[TemplateType.DOCUMENT_PART]}
                    onSelect={handleTemplateSelect}
                    onCancel={handleTemplateCancel}
                />
            )}
            {showExportMarkdown && (
                <ExportMarkdownDialog document={document} onClose={handleExportMarkdownClose}/>
            )}
        </>
    )
}
